import { useClient } from "Client/ClientAndUserProvider"
import { exhaustive } from "exhaustive"
import { isNumber } from "lodash"
import { FC, ForwardedRef, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMediaQuery } from "react-responsive"
import { useSearchParams } from "react-router-dom"
import { cls } from "Shared/cls"
import { WidgetRendererV1 } from "Shared/Widget/Widgets"
import { z } from "zod"
import { useAuth } from "../../Auth/AuthContext"
import { useConsumerCatalog } from "../../Client/ConsumerCatalogContext"
import { useConsumer } from "../../Client/ConsumerContext"
import { ProductSelectionMode } from "../../Client/FeatureTypes"
import { ProductDefinition } from "../../Client/ProductDefinitionsByCategories"
import { ConsumerState } from "../../CustomerPortal/CustomerPortalConsumerInformation/CustomerPortalConsumerInformation"
import { useGoogleTagManager } from "../../GoogleTagManagerContext"
import { getLogger } from "../../Logging/getLogger"
import { CalculationProduct } from "../../QuantityCalculator/QuantityCalculator"
import { EventQueue } from "../../Shared/eventQueue"
import { useBrandedLocalStorage } from "../../Shared/useBrandedLocalStorage"
import { removeModalOpen, removeModalOpenClass } from "../Components/ModulePopup/ModulePopup"
import { DateSelectModule } from "../DateSelectModule/DateSelectModule"
import {
	ProductInformationAndSelectionModule,
	ProductSelectionReturnValue,
} from "../ProductInformationAndSelectionModule/ProductInformationAndSelectionModule"
import { TimeSelectModule } from "../TimeSelectModule/TimeSelectModule"
import { useBasket } from "./BasketProvider"
import style from "./OrderContainer.module.css"
import {
	modalOpenQueryKey,
	orderItemEditDateEventName,
	orderItemEditProjectEventName,
	orderItemEditTimeEventName,
} from "./OrderContainerWrapper"
import {
	allowedDateOrTimeSelectValues,
	handleQueryParamsOrderItemEditDate,
	handleQueryParamsOrderItemEditTime,
} from "./ProductSelectionLogic"
import {
	ProductDefinitionWithPrice,
	resolveProductDefinitionsWithPriceData,
} from "./resolveProductDefinitionsWithPriceData"
import { CategorySelection } from "./SubComponents/CategorySelection"
import { ClientConsumerSelection } from "./SubComponents/ClientConsumerSelection"
import { ProductSelection } from "./SubComponents/ProductSelection"
import { ProjectSelection, ProjectSelectionRefProps } from "./SubComponents/ProjectSelection"
import { ServiceSelection } from "./SubComponents/ServiceSelection"

const logger = getLogger("OrderContainer")

type Props = {}

export enum OpenModalTypes {
	NewProject = "new_proj",
	OldProject = "old_proj",
}

export type OrderItemTotalPrices = Record<
	string,
	{ price: number; tax: number; amountOfArticles: number; discount: number; taxDiscount: number }
>

type ProductInfoAndSelection = {
	productId: string
	descriptionOpen: boolean
}

export const OrderContainer: FC<Props> = () => {
	const auth = useAuth()
	const client = useClient()
	const consumerCatalog = useConsumerCatalog()
	const consumer = useConsumer()
	const basket = useBasket()
	const isMobileSize = useMediaQuery({ query: `(max-width: 1100px)` })
	const gtm = useGoogleTagManager()

	const [showDateSelectModule, setShowDateSelectModule] = useState(false)
	const [showTimeSelectModule, setShowTimeSelectModule] = useState(false)

	const [, setTick] = useState(0)

	const placeOrderAllowed = useMemo(() => {
		if (auth.IsLoggedInConsumer) {
			if (!consumer) {
				return false
			}

			const currentConsumer = auth.Me?.consumers?.find((x) => x.id === consumer.consumerId)

			if (!currentConsumer || (currentConsumer && currentConsumer.state !== ConsumerState.Active)) {
				return false
			}
		}

		return true
	}, [auth.IsLoggedIn, consumer])

	const [queryParams, setQueryParams] = useSearchParams()
	const previousQueryParams = useRef<URLSearchParams>(queryParams)
	const queriedCategory = queryParams.get("category")

	const selectedCategoryNameToUse = (() => {
		if (queriedCategory) {
			return queriedCategory
		}

		const categoryKey = client.categoryKeys[0]

		if (!categoryKey) {
			return null
		}

		return client.categories[categoryKey]?.name || null
	})()
	const selectedCategory = client.findCategoryByName(selectedCategoryNameToUse)
	const selectedServiceId = queryParams.get("service") || null

	const [productInfoAndSelection, setProductInfoAndSelection] = useState<ProductInfoAndSelection | null>(null)

	const availableProducts: ProductDefinitionWithPrice[] = useMemo(() => {
		if (selectedCategoryNameToUse && selectedCategory) {
			const categoryProducts: ProductDefinition[] = Object.values(selectedCategory.products)
			categoryProducts.sort((a, b) => a.order - b.order)
			return (
				resolveProductDefinitionsWithPriceData(
					categoryProducts,
					selectedServiceId,
					consumerCatalog,
					client,
					auth,
				) ?? []
			)
		} else {
			return []
		}
	}, [selectedCategoryNameToUse, selectedCategory, selectedServiceId, consumerCatalog, client, auth])

	const projectSelectionComponentRef: ForwardedRef<ProjectSelectionRefProps> = useRef(null)

	const [showRegularBasket] = useBrandedLocalStorage("show-regular-basket", z.boolean(), {
		defaultValue: false,
	})

	const openProduct =
		productInfoAndSelection && selectedCategory ? selectedCategory.products[productInfoAndSelection.productId] : null

	const closeAllModals = useCallback((closeMobileBasket: boolean = true) => {
		projectSelectionComponentRef.current?.setShowProjectSelectModule(false, () => {})

		projectSelectionComponentRef.current?.setEditProject(null)

		setProductInfoAndSelection(null)

		setShowDateSelectModule(false)

		setShowTimeSelectModule(false)

		if (closeMobileBasket) {
			basket.functions.setMobileBasketShown(false)
		}
	}, [])

	const setQueryParamsUrl = useCallback(
		(
			category: string,
			service?: string | null,
			product?: string | null,
			openModal?: OpenModalTypes,
			removeModal?: boolean,
		) => {
			if (queryParams.get("category") !== category) {
				queryParams.set("category", category)
			}

			if (!service) {
				queryParams.delete("service")
			} else if (queryParams.get("service") !== service) {
				queryParams.set("service", service)
			}

			if (!product) {
				queryParams.delete("product")
			} else {
				queryParams.set("product", product)
			}

			if (openModal) {
				queryParams.set(modalOpenQueryKey, openModal)
			}

			if (removeModal && queryParams.has(modalOpenQueryKey)) {
				queryParams.delete(modalOpenQueryKey)
			}
			setQueryParams(queryParams)
		},
		[queryParams, setQueryParams],
	)

	useEffect(() => {
		const element = document.getElementById("mainLayoutPageContent")
		if (element) {
			element.scrollTop = 0
		}
	}, [])

	useEffect(() => {
		const eventListenerIds: string[] = []
		eventListenerIds.push(
			EventQueue.addEventListener("quantityCalculatorBasket", (calcProducts: CalculationProduct[]) => {
				const vals: ProductSelectionReturnValue[] = []
				calcProducts.forEach((calcProduct) => {
					if (calcProduct.type === "Goods" && calcProduct.selectedPackagingMethod) {
						vals.push({
							productId: calcProduct.productId,
							name: calcProduct.productName,
							category: calcProduct.productCategoryName,
							packagingMethods: {
								[calcProduct.selectedPackagingMethod.packagingMethodId]: calcProduct.selectedAmount,
							},
						})
					}
				})

				if (!basket.values.selectedProject) {
					projectSelectionComponentRef?.current?.setShowProjectSelectModule(true, (project) => {
						basket.functions.addProduct(vals, 1, project)
					})
				} else {
					basket.functions.addProduct(vals)
				}
			}),
		)

		return () => {
			eventListenerIds.forEach((id) => EventQueue.removeEventListener(id))
		}
	}, [basket.functions, basket.values.selectedProject])

	useEffect(() => {
		if (selectedCategory && selectedServiceId) {
			exhaustive(selectedCategory, "type", {
				WasteCategory: (it) => {
					if (!it.serviceIds.includes(selectedServiceId)) {
						setQueryParamsUrl(selectedCategory.name, null)
					}
				},
				GoodsCategory: () => {
					setQueryParamsUrl(selectedCategory.name, null)
				},
			})
		}
	}, [selectedCategory, selectedServiceId, setQueryParamsUrl])

	useEffect(() => {
		const eventListenerIds: string[] = []
		eventListenerIds.push(
			EventQueue.addEventListener("closeAllModals", () => {
				closeAllModals()
			}),
		)

		return () => {
			eventListenerIds.forEach((id) => EventQueue.removeEventListener(id))
		}
	}, [closeAllModals])

	useEffect(() => {
		let productQueryId = queryParams.get("product")
		if (!productQueryId || (selectedCategory && !selectedCategory.products[productQueryId])) {
			setProductInfoAndSelection(null)
		} else if (selectedCategory && productQueryId && !queryParams.has(modalOpenQueryKey) && !productInfoAndSelection) {
			const product = availableProducts.find((it) => it.id === productQueryId)
			if (product != null) {
				setProductInfoAndSelection({
					productId: product.id,
					descriptionOpen: false,
				})
			}
		}
	}, [availableProducts, productInfoAndSelection, queryParams, selectedCategory])

	useEffect(() => {
		let productQueryId = queryParams.get("product")
		if (productQueryId && projectSelectionComponentRef?.current?.isOpen) {
			projectSelectionComponentRef?.current.close()
		}
	}, [productInfoAndSelection, queryParams])

	useEffect(() => {
		const listener1 = EventQueue.addEventListener<{ orderItemIndex: number }>(orderItemEditProjectEventName, (data) => {
			const index = data.orderItemIndex
			if (isNumber(index) && index > -1) {
				basket.functions.setSelectedOrderItem(index)
				setTimeout(() => {
					projectSelectionComponentRef.current?.setShowProjectSelectModule(true, (project) => {
						basket.functions.onProjectSelected(project, false, null, index)
					})
				}, 50)
			}
		})

		const listener2 = EventQueue.addEventListener<{ orderItemIndex: number }>(orderItemEditDateEventName, (data) => {
			const index = data.orderItemIndex
			if (isNumber(index) && index > -1) {
				handleQueryParamsOrderItemEditDate(index, basket, isMobileSize, () => {
					setShowDateSelectModule(true)
				})
			}
		})

		const listener3 = EventQueue.addEventListener<{ orderItemIndex: number }>(orderItemEditTimeEventName, (data) => {
			const index = data.orderItemIndex
			if (isNumber(index) && index > -1) {
				handleQueryParamsOrderItemEditTime(index, basket, client, isMobileSize, () => {
					setShowTimeSelectModule(true)
				})
			}
		})

		return () => {
			EventQueue.removeEventListener(listener1)
			EventQueue.removeEventListener(listener2)
			EventQueue.removeEventListener(listener3)
		}
	}, [basket, basket.functions, client, isMobileSize])

	useEffect(() => {
		const modalOpenParam = queryParams.get(modalOpenQueryKey) as OpenModalTypes | null
		if (modalOpenParam) {
			exhaustive(modalOpenParam, {
				[OpenModalTypes.NewProject]: () => {
					projectSelectionComponentRef.current?.setEditProject({ isNew: true, project: {} })
				},
				[OpenModalTypes.OldProject]: () => {
					projectSelectionComponentRef.current?.setShowProjectSelectModule(true, () => {})
				},
				_: () => {},
			})
		}
	}, [basket, client, isMobileSize, queryParams])

	useEffect(() => {
		if (previousQueryParams.current?.has(modalOpenQueryKey) && !queryParams.has(modalOpenQueryKey)) {
			closeAllModals()
			removeModalOpen(queryParams, setQueryParams)
		}

		previousQueryParams.current = new URLSearchParams(queryParams)
	}, [closeAllModals, queryParams, setQueryParams])

	if (client.categoryKeys.length <= 0) {
		return (
			<div className={style.noProductsWrapper}>
				<div className={style.noProductsMessage}>
					{client.clientInfo.clientName} har tyvärr inga produkter upplagda än, men det kommer snart!
				</div>
			</div>
		)
	}

	const onProductSelected = (data: ProductSelectionReturnValue) => {
		const res = basket.functions.addProduct([data])

		if (res === "no-project") {
			projectSelectionComponentRef.current?.setShowProjectSelectModule(true, (project) => {
				basket.functions.addProduct([data], 1, project, undefined, undefined, undefined, undefined, true)
			})
		}
	}

	function modalElements(): JSX.Element | null {
		const orderItem =
			basket.values.selectedOrderItemIndex !== null ? basket.orderItems[basket.values.selectedOrderItemIndex] : null
		const categoryOfSelectedOrderItem = orderItem ? client.categories[orderItem.category] : null

		const openProduct = availableProducts.find((it) => it.id === productInfoAndSelection?.productId)
		const descriptionOpen = productInfoAndSelection?.descriptionOpen ?? false

		return (
			<>
				{openProduct != null && selectedCategory ? (
					<ProductInformationAndSelectionModule
						product={openProduct}
						descriptionOpen={descriptionOpen}
						category={selectedCategory}
						service={
							selectedServiceId !== null && selectedCategory?.type === "WasteCategory"
								? selectedCategory?.services[selectedServiceId]
								: undefined
						}
						possibleServices={
							selectedCategory?.type === "WasteCategory" ? selectedCategory?.services || {} : {}
						}
						onClose={() => {
							setProductInfoAndSelection(null)
							setQueryParamsUrl(selectedCategory!.name, selectedServiceId || "", "", undefined, true)
							removeModalOpenClass()
						}}
						onProductSelected={(data) => {
							onProductSelected(data)
						}}
						onSetSelectedServiceId={onSetSelectedServiceId}
					/>
				) : null}
				{showDateSelectModule && orderItem ? (
					<DateSelectModule
						onDateSelected={(date) =>
							basket.functions.setDateOnOrderItem(basket.values.selectedOrderItemIndex, date)
						}
						allowedValues={allowedDateOrTimeSelectValues(
							basket,
							client,
							categoryOfSelectedOrderItem || null,
							"date",
						)}
						defaultValue={orderItem.date}
						onClose={() => {
							setShowDateSelectModule(false)
							removeModalOpenClass()
						}}
					/>
				) : null}
				{showTimeSelectModule && orderItem ? (
					<TimeSelectModule
						onTimeSelected={(timeslotId, timeName, specificTime) =>
							basket.functions.setTimeOnOrderItem(
								basket.values.selectedOrderItemIndex,
								timeslotId,
								timeName,
								specificTime,
							)
						}
						allTimeSlots={client.possibleTimeSlots}
						allowedValues={allowedDateOrTimeSelectValues(
							basket,
							client,
							categoryOfSelectedOrderItem || null,
							"time",
						)}
						defaultValue={orderItem.time}
						onClose={() => {
							setShowTimeSelectModule(false)
							removeModalOpenClass()
						}}
					/>
				) : null}
			</>
		)
	}

	function onSetShowProjectSelectModule() {
		setQueryParamsUrl(selectedCategoryNameToUse || "", selectedServiceId || "", "", OpenModalTypes.OldProject)
	}

	function onSetShowProjectInputModule() {
		setQueryParamsUrl(selectedCategoryNameToUse || "", selectedServiceId || "", "", OpenModalTypes.NewProject)
	}

	function onSetSelectedCategory(categoryName: string) {
		let category = client.findCategoryByName(categoryName)
		if (category == null) {
			logger.error("Selected a non-existing category:", categoryName)
			return
		}
		exhaustive(category, "type", {
			WasteCategory: (it) => {
				const fullPage =
					client.features.orderUiProductSelectionMode === ProductSelectionMode.FullPage &&
					!!it.productSelectionConfig &&
					it.productSelectionConfig.steps.length > 0

				let service = null
				if (!fullPage && it.serviceIds.length === 1) {
					service = it.serviceIds[0]
				}

				if (queryParams.get("category") !== categoryName || queryParams.get("service") !== service) {
					setQueryParamsUrl(categoryName, service)
				}
			},
			GoodsCategory: () => {
				setQueryParamsUrl(categoryName, null)
			},
			_: () => {
				logger.error("Undefined category selected, trying to continue: ", category)
			},
		})

		if (
			basket.values.selectedOrderItemIndex !== null &&
			basket.orderItems[basket.values.selectedOrderItemIndex]?.category !== ""
		) {
			basket.functions.setSelectedOrderItem(null)
		}
	}

	function onSetSelectedServiceId(selectedCategory: string, serviceId: string | null) {
		setQueryParamsUrl(selectedCategory, serviceId || undefined, openProduct?.id)
	}

	function onProductClick(selectedCategory: string, product: ProductDefinitionWithPrice) {
		gtm.pushEvent("view_item", {
			ecommerce: {
				items: [
					{
						currency: "SEK",
						item_id: product.id,
						item_name: product.name,
						item_category: product.categoryName,
					},
				],
			},
		})

		setProductInfoAndSelection({ productId: product.id, descriptionOpen: false })
		setQueryParamsUrl(selectedCategory, selectedServiceId || "", product.id, undefined, true)
	}

	function onProductCardClick(selectedCategory: string, product: ProductDefinitionWithPrice) {
		gtm.pushEvent("view_item", {
			ecommerce: {
				items: [
					{
						currency: "SEK",
						item_id: product.id,
						item_name: product.name,
						item_category: product.categoryName,
					},
				],
			},
		})

		setProductInfoAndSelection({ productId: product.id, descriptionOpen: true })
		setQueryParamsUrl(selectedCategory, selectedServiceId || "", product.id, undefined, true)
	}

	const onProductIncrementorChange = (
		buttonClicked: "addClick" | "removeClick" | "text",
		product: ProductDefinition,
		value: number,
		currentValue: number,
		category: string,
		service: string,
	) => {
		const res = basket.functions.onProductIncrementorChange(
			buttonClicked,
			product,
			value,
			currentValue,
			category,
			service,
		)

		if (res === "no-project") {
			projectSelectionComponentRef.current?.setShowProjectSelectModule(true, (project) => {
				if (project) {
					basket.functions.onProductIncrementorChange(
						buttonClicked,
						product,
						value,
						currentValue,
						category,
						service,
						project,
					)
				}
			})
		}
	}

	return (
		<>
			{modalElements()}
			<div
				className={cls(style.wrapper, {
					[style.regularBasketHidden]: !showRegularBasket,
				})}>
				<div className={style.content}>
					{client.welcomeWidget != null ? <WidgetRendererV1 widgets={[client.welcomeWidget]} /> : null}
					<ul
						className={cls(style.timeline, style.mobileOnly)}
						style={{ marginBlockStart: 0, marginBlockEnd: 0, marginBottom: "20px" }}>
						<li className={style.current}>
							<div />
						</li>
						<div className={style.timelineLine} />
						<li>
							<div />
						</li>
						<div className={style.timelineLine} />
						<li>
							<div />
						</li>
					</ul>
					<ClientConsumerSelection />
					{placeOrderAllowed ? (
						<ProjectSelection
							project={basket.values.selectedProject}
							onSetShowProjectSelectModule={onSetShowProjectSelectModule}
							onSetShowProjectInputModule={onSetShowProjectInputModule}
							ref={projectSelectionComponentRef}
							currentOrderItemProject={
								basket.values.selectedOrderItemIndex != null &&
								basket.orderItems[basket.values.selectedOrderItemIndex]
									? basket.orderItems[basket.values.selectedOrderItemIndex]?.project || null
									: basket.values.selectedProject || null
							}
							onProjectSelected={(project, isNew, productSelectionReturnValues) => {
								basket.functions.onProjectSelected(project, isNew, productSelectionReturnValues, null)
							}}
							onProjectInputClose={() => {}}
							onProjectSelectClose={() => {
								if (basket.values.mobileBasketShown) {
									return
								}
								/*
									Don't really like using the setTick trick, but very rarely I need to do a specific
									thing, such as triggering a re-render to ensure an incrementor is rendered correctly.

									This solution is way easier than passing a reference to the incrementor between
									several functions and files and calling its internal functions to make sure it's
									rendered correctly...
								 */
								setTick((x) => x + 1)
							}}
						/>
					) : null}

					<CategorySelection
						selectedCategory={selectedCategoryNameToUse}
						onSetSelectedCategory={onSetSelectedCategory}
					/>
					<ServiceSelection
						selectedCategory={selectedCategoryNameToUse}
						selectedServiceId={selectedServiceId}
						onSetSelectedServiceId={onSetSelectedServiceId}
					/>
					{selectedCategoryNameToUse ? (
						<ProductSelection
							selectedCategoryName={selectedCategoryNameToUse}
							selectedServiceId={selectedServiceId}
							availableProducts={availableProducts}
							onProductClick={onProductClick}
							onProductCardClick={onProductCardClick}
							onProductIncrementorChange={onProductIncrementorChange}
						/>
					) : null}
				</div>
			</div>
		</>
	)
}
